/**
 * Modal — scrim + glass card with header (eyebrow + title + close),
 * scrollable body, optional sticky footer for action buttons.
 *
 * The card is the dialog: it is named by its title (or `ariaLabel`), takes
 * focus when it opens (a child that focused itself keeps it; otherwise the
 * first control in the body, then ✕), keeps Tab and Shift+Tab inside, and
 * hands focus back to whatever opened it when it closes. Only the topmost
 * open modal answers Escape and Tab, so a dialog opened from a dialog closes
 * on its own.
 *
 * Click-outside dismisses; Escape dismisses.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import IconButton from './IconButton';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  eyebrow?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  width?: number | string;
  closeOnBackdrop?: boolean;
  /** Close when Escape is pressed. Set false for data-entry forms that
   *  should not discard in-progress input on a stray Esc. */
  closeOnEsc?: boolean;
  variant?: 'default' | 'compact';
  ariaLabel?: string;
}

const dimsByVariant = {
  default: {
    radius:        'var(--radius-xl)',
    headerPadding: '20px 24px 16px',
    bodyPadding:   '20px 24px',
    footerPadding: '14px 24px',
    titleSize:     18,
  },
  compact: {
    radius:        'var(--radius-lg)',
    headerPadding: '16px 18px 12px',
    bodyPadding:   '14px 18px',
    footerPadding: '12px 18px',
    titleSize:     16,
  },
} as const;

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Open modals, oldest first. Only the last one handles Escape and Tab. */
const openModals: object[] = [];

/** Controls that belong to this dialog (not to a dialog opened inside it). */
const focusablesIn = (panel: HTMLElement): HTMLElement[] =>
  Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.closest('[role="dialog"]') === panel);

const activeElement = (): HTMLElement | null =>
  typeof document !== 'undefined' && document.activeElement instanceof HTMLElement ? document.activeElement : null;

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  width = 560,
  closeOnBackdrop = true,
  closeOnEsc = true,
  variant = 'default',
  ariaLabel,
}) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  // Latest callbacks for the key handler, so a new inline onClose never re-runs the open effect.
  const onCloseRef = useRef(onClose);
  const closeOnEscRef = useRef(closeOnEsc);
  useEffect(() => {
    onCloseRef.current = onClose;
    closeOnEscRef.current = closeOnEsc;
  });

  // The element that had focus when the modal opened. Captured while rendering
  // the open (React's "adjust state when a prop changes" pattern), because a
  // child with autoFocus takes focus before any effect here runs.
  const [openSeen, setOpenSeen] = useState(open);
  const [opener, setOpener] = useState<HTMLElement | null>(() => (open ? activeElement() : null));
  if (open !== openSeen) {
    setOpenSeen(open);
    setOpener(open ? activeElement() : null);
  }

  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    openModals.push(token);
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      const first = (bodyRef.current && focusablesIn(panel).find((el) => bodyRef.current!.contains(el))) || closeRef.current || panel;
      first.focus({ preventScroll: true });
    }

    const onKey = (e: KeyboardEvent) => {
      if (openModals[openModals.length - 1] !== token || !panelRef.current) return;
      if (e.key === 'Escape') {
        if (closeOnEscRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusablesIn(panelRef.current);
      if (items.length === 0) {
        e.preventDefault();
        panelRef.current.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      const inside = !!current && panelRef.current.contains(current);
      if (e.shiftKey && (!inside || current === first)) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && (!inside || current === last)) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const at = openModals.indexOf(token);
      if (at >= 0) openModals.splice(at, 1);
    };
  }, [open]);

  // Give focus back to the opener once the dialog is gone (closed or unmounted).
  useEffect(() => {
    if (!open || !opener) return undefined;
    return () => {
      if (opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open, opener]);

  if (!open) return null;

  const dims = dimsByVariant[variant];

  return (
    <div
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 'var(--z-modal)' as unknown as number,
        padding: 24,
        animation: 'fadeIn 180ms var(--ease-standard)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={!ariaLabel && title ? titleId : undefined}
        tabIndex={-1}
        style={{
          width: '100%',
          maxWidth: width,
          maxHeight: 'calc(100vh - 48px)',
          background: '#fff',
          borderRadius: dims.radius,
          border: '1px solid var(--border-1)',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--font-sans)',
          color: 'var(--fg-1)',
          animation: 'fadeInUp 220ms var(--ease-standard)',
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        {(title || eyebrow) && (
          <header
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: dims.headerPadding,
              borderBottom: '1px solid var(--border-subtle)',
              flexShrink: 0,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              {eyebrow && (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--fg-3)',
                    marginBottom: 4,
                  }}
                >
                  {eyebrow}
                </div>
              )}
              {title && (
                <div
                  id={titleId}
                  style={{
                    fontSize: dims.titleSize,
                    fontWeight: 700,
                    color: 'var(--fg-1)',
                    letterSpacing: '-0.01em',
                    fontFamily: 'var(--font-display)',
                  }}
                >
                  {title}
                </div>
              )}
            </div>
            <IconButton ref={closeRef} size="sm" variant="ghost" label="Close" onClick={onClose}>
              ✕
            </IconButton>
          </header>
        )}

        <div
          ref={bodyRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: dims.bodyPadding,
          }}
        >
          {children}
        </div>

        {footer && (
          <footer
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: dims.footerPadding,
              borderTop: '1px solid var(--border-subtle)',
              background: 'var(--cg-neutral-25)',
              flexShrink: 0,
            }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
};

export default Modal;
